import { SavedPermissionRecovery } from "../../components/dashboard/SavedPermissionRecovery";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Logo } from "../../components/dashboard/Logo";
import { Skeleton } from "../../components/dashboard/Skeleton";
import {
  ApiKeyConnectModal,
  type ApiKeySubmitResult,
} from "../../components/dashboard/integrations/ApiKeyConnectModal";
import {
  BYO_OAUTH_FIELDS,
  ByoOauthPreamble,
} from "../../components/dashboard/integrations/ByoOauthConnect";
import { IntegrationGuide } from "../../components/dashboard/integrations/IntegrationGuide";
import { useResources, type ResourceView } from "../../hooks/useResources";
import { apiKeyConnectCopy } from "../../lib/apiKeyFields";
import { pickIntegrationGuide } from "../../lib/integrationGuide";
import {
  ConnectedAccounts,
  ConnectCta,
  InterestForm,
} from "../../components/dashboard/integrations/ConnectPanels";
import connect from "../../components/dashboard/integrations/connect.module.css";
import { PreConnectStepModal } from "../../components/dashboard/integrations/PreConnectStepModal";
import { SlotGate } from "../../components/dashboard/integrations/SlotGate";
import { CAL_BOOK_20MIN_URL } from "../../config/links";
import { MESSAGE_CHANNELS } from "../../config/messageChannels";
import { PRE_CONNECT_STEPS, type PreConnectStep } from "../../config/preConnectSteps";
import { useInterest } from "../../hooks/useAnalytics";
import { useIntegrations } from "../../hooks/useIntegrations";
import {
  useIntegrationTools,
  type ToolsStatus,
} from "../../hooks/useIntegrationTools";
import { useClaws } from "../../hooks/useClaws";
import { FEATURE_FLAGS, flagEnabled, useAuth } from "../../hooks/useAuth";
import { useToolPermissions } from "../../hooks/useToolPermissions";
import {
  buildPermissionOverlay,
  effectiveToolPermission,
  type PermissionOverlay,
} from "../../lib/toolPermissionOverlay";
import { getCsrfHeader } from "../../lib/csrf";
import { ARRIVAL_PARAMS, arrivalNotice, readArrival, type Arrival } from "../../lib/integrationArrival";
import { AUTH_TYPE, canStartConnect } from "../../types/integration";
import type {
  ApiKeyField,
  Connection,
  ConnectionStatus,
  Integration,
  IntegrationAvailability,
  IntegrationTool,
  ToolPermission,
} from "../../types/integration";
import styles from "./IntegrationDetail.module.css";

// Tools shown per page. Big toolkits (Gmail, Slack, GitHub) expose dozens to
// hundreds, so the list paginates rather than dumping everything at once.
const TOOLS_PER_PAGE: number = 10;

// Catalog slugs that are also message channels: their chat-channel setup lives in
// the Message Channels section, so the detail page cross-links there instead of
// offering channel setup inline.
const MESSAGE_CHANNEL_SLUGS: ReadonlySet<string> = new Set(MESSAGE_CHANNELS.map((c) => c.slug));

// IP-152/IP-206 polling: cadence + cap. While a connect is 'pending' we poll
// the per-tenant endpoint to pick up the gateway poller's (IP-139, ~30s) flip
// to active/error. Cadence starts tight — a hosted-OAuth round trip usually
// completes in well under a minute — then relaxes to keep request volume sane
// over the longer wait. The window outlasts Composio's ~10-min INITIATED
// expiry: if the connect hasn't resolved by the cap we cancel the lapsed
// attempt (see the poll effect) so the UI reverts on its own without a refresh.
const CONNECTION_POLL_INTERVAL_MS: number = 5_000;
const CONNECTION_POLL_RELAX_AFTER_MS: number = 120_000;
const CONNECTION_POLL_RELAXED_INTERVAL_MS: number = 20_000;
const CONNECTION_POLL_DURATION_MS: number = 11 * 60_000;
// How often we check whether the user closed the OAuth tab without finishing.
const OAUTH_TAB_WATCH_INTERVAL_MS: number = 1_000;

// Per-category "what it does, once connected" value props. Editorial copy
// (not synced per-integration), so it reads as a category promise rather than
// app-specific detail. Falls back to fallbackValueProps for unlisted categories.
const VALUE_PROPS_BY_CATEGORY: Record<string, string[]> = {
  Messaging: [
    "Two-way chat with your agent from any device.",
    "Threaded conversations preserved across sessions.",
    "Group + DM support; admin controls per channel.",
  ],
  Productivity: [
    "Read, draft, and send on your behalf.",
    "Calendar-aware scheduling that respects focus time.",
    "Files synced + searchable across modalities.",
  ],
  Voice: [
    "Natural-sounding calls in your brand voice.",
    "Inbound + outbound coverage with handoff to humans.",
    "Call summaries land in your CRM automatically.",
  ],
  CRM: [
    "Contacts, deals, and pipelines kept in sync.",
    "Agent enriches records as it works.",
    "Triggers + sequences fire from agent decisions.",
  ],
  Finance: [
    "Invoices and reconciliation, hands-off.",
    "Categorisation that learns your account map.",
    "Audit-ready exports on demand.",
  ],
  Marketing: [
    "Spend management with daily caps.",
    "Creative routing tied to performance signals.",
    "Reporting that explains the why, not just the what.",
  ],
  Scheduling: [
    "Bookings written directly to the right calendar.",
    "Round-robin + buffer rules respected.",
    "Cancellations + reschedules handled gracefully.",
  ],
  Knowledge: [
    "Read your team wiki, draft new entries.",
    "Citations and confidence levels on answers.",
    "Stale-page detection + suggested rewrites.",
  ],
  Sales: [
    "Prospect lists kept current and deduped.",
    "Outreach sequences tuned to reply signals.",
    "Pipeline forecasting that the team can argue with.",
  ],
};

const fallbackValueProps = (integration: Integration): string[] => [
  `Connect ${integration.title} so your agent can act inside it directly.`,
  "Authenticated end-to-end — credentials never leave your tenant.",
  "Toggle off any time from this page.",
];

// IP-217: a "where do I get my API key" link, shown in the auth section and the
// connect modal. Sourced entirely from Composio data (no curated links — we
// can't verify a hand-maintained list of vendor key screens, and a wrong link
// is worse than none). Resolved by precedence:
//   1. a URL embedded in one of Composio's field descriptions (some inline one,
//      e.g. "Generate at https://app.incident.io/settings/api-keys");
//   2. the app's homepage (Composio's meta.app_url), the always-present floor.
// Returns undefined only when we have neither (then the section just shows the
// generic copy + the per-field description hints in the modal).
const URL_IN_TEXT = /\bhttps?:\/\/[^\s)]+/i;

const firstDescriptionUrl = (
  fields: ReadonlyArray<ApiKeyField>,
): string | undefined => {
  for (const f of fields) {
    const match = f.description?.match(URL_IN_TEXT);
    // Trim trailing sentence punctuation a URL at the end of a sentence picks up.
    if (match) return match[0].replace(/[.,);]+$/, "");
  }
  return undefined;
};

const resolveKeyGuide = (
  title: string,
  fields: ReadonlyArray<ApiKeyField>,
  appUrl: string | null,
): { href: string; linkLabel: string } | undefined => {
  const fromDescription = firstDescriptionUrl(fields);
  if (fromDescription) {
    return { href: fromDescription, linkLabel: `Get your ${title} API key` };
  }
  // Homepage fallback: less precise (not the key screen), so the label reflects
  // that the user lands on the app rather than directly on a key page.
  if (appUrl) return { href: appUrl, linkLabel: `Go to ${title}` };
  return undefined;
};

const Hero = ({
  integration,
  configured,
  toolCount,
  showBadge,
  adminControl,
}: {
  integration: Integration;
  configured: boolean;
  toolCount: number | null;
  showBadge: boolean;
  adminControl?: JSX.Element | undefined;
}): JSX.Element => {
  return (
    <header className={styles.hero}>
      <span className={styles.logoChip}>
        <Logo
          slug={integration.slug}
          size={64}
          logoUrl={integration.ourLogoUrl ?? integration.logoUrl ?? null}
          {...(integration.iconBrandHex !== undefined ? { brandHex: integration.iconBrandHex } : {})}
        />
      </span>
      <div className={styles.titleBlock}>
        <span className={styles.eyebrow}>// {integration.category.toUpperCase()}</span>
        <h1 className={styles.name}>{integration.title}</h1>
        <p className={styles.blurb}>{integration.description}</p>
      </div>
      {/* Top-right: row 1 is the tool-count chip + a single status badge; row 2
          (admins only) carries the Auth config + Connectability controls, so
          they never compact the title/description. All gated on the connect
          feature flag (showBadge) so the page can ship dark. V49: for an admin
          on an admin-only integration the status badge reads "Admins only"
          (purple) in place of "Ready to go" — the admin gate is implied. */}
      <div className={styles.heroBadgeStack}>
        <div className={styles.heroBadgeRow}>
          {showBadge && toolCount !== null && toolCount > 0 ? (
            <span className={styles.metaChip}>
              {toolCount.toLocaleString()} {toolCount === 1 ? "tool" : "tools"}
            </span>
          ) : null}
          {showBadge ? (
            integration.adminOnly === true ? (
              <span className={`${styles.heroBadge} ${styles.heroBadgeAdmin}`}>Admins only</span>
            ) : (
              <span
                className={`${styles.heroBadge} ${configured ? styles.heroBadgeOk : styles.heroBadgeNo}`}
              >
                {configured ? "Ready to go" : "Request access"}
              </span>
            )
          ) : null}
        </div>
        {adminControl ? <div className={styles.heroAdminRow}>{adminControl}</div> : null}
      </div>
    </header>
  );
};

// IP-205 + V49: the four connectability states, mirroring the server's
// ConnectabilityState enum (the key IS the wire value). "Automatic" follows
// Composio's live managed/API-key status; "Everyone" / "Not connectable" pin
// it on or off; "Admins only" pins it on for admins while regular users see
// it as "Request access" (prod-testing gate, e.g. a BYO OAuth app still in
// the provider's review). A pinned choice survives catalog re-syncs.
export type ConnectabilityChoice = {
  key: "auto" | "everyone" | "admins" | "off";
  label: string;
};
const CONNECTABILITY_OPTIONS: ReadonlyArray<ConnectabilityChoice> = [
  { key: "auto", label: "Automatic" },
  { key: "everyone", label: "Everyone" },
  { key: "admins", label: "Admins only" },
  { key: "off", label: "Not connectable" },
];

const activeChoiceKey = (override: boolean | null, adminOnly: boolean): ConnectabilityChoice["key"] => {
  if (adminOnly) return "admins";
  if (override === null) return "auto";
  return override ? "everyone" : "off";
};

// IP-205: admin-only "Connectability" button that opens a small dropdown to set
// the connectability override. Closes on outside click or Escape; the option
// matching the current state is checked.
const AdminConnectabilityMenu = ({
  override,
  adminOnly,
  busy,
  onSelect,
}: {
  override: boolean | null;
  adminOnly: boolean;
  busy: boolean;
  onSelect: (choice: ConnectabilityChoice) => void;
}): JSX.Element => {
  const [open, setOpen] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const activeKey = activeChoiceKey(override, adminOnly);
  const choose = (choice: ConnectabilityChoice): void => {
    setOpen(false);
    if (choice.key !== activeKey) onSelect(choice);
  };

  return (
    <div className={styles.adminMenu} ref={ref}>
      <button
        type="button"
        className={styles.adminMenuTrigger}
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        Connectability
        <span aria-hidden>▾</span>
      </button>
      {open ? (
        <div className={styles.adminMenuPopover} role="menu">
          <span className={styles.adminMenuLabel}>Mark connectability as</span>
          {CONNECTABILITY_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              role="menuitemradio"
              aria-checked={activeKey === opt.key}
              className={styles.adminMenuItem}
              onClick={() => choose(opt)}
            >
              <span className={styles.adminMenuCheck} aria-hidden>
                {activeKey === opt.key ? "✓" : ""}
              </span>
              {opt.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

// V49: admin-only auth-config registration for Composio BYO custom-OAuth
// toolkits (Xero, Meta Ads, ...). Lets the admin paste an `ac_...` id; the
// gateway validates it against Composio before persisting (wrong project /
// wrong toolkit / disabled all come back as a 422 with a reason). The status
// is fetched LAZILY on first open (not on every detail-page mount), so the
// trigger dot stays neutral until then; a non-Composio integration reports
// "not applicable" once opened (its status endpoint 404s).
const AdminAuthConfigControl = ({ slug }: { slug: string }): JSX.Element => {
  const [open, setOpen] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [supported, setSupported] = useState<boolean>(true);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [input, setInput] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // Lazy status fetch: only the first time the popover opens (and once per
  // slug). No work on mount, so browsing detail pages costs nothing here.
  useEffect(() => {
    if (!open || loaded) return undefined;
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const res = await fetch(
          `/api/admin/integration-catalog/${encodeURIComponent(slug)}/composio-auth-config`,
          { credentials: "include" },
        );
        if (cancelled) return;
        if (res.status === 404) {
          setSupported(false);
        } else if (res.ok) {
          const data = (await res.json()) as { authConfigId: string | null };
          if (!cancelled) setCurrentId(data.authConfigId ?? null);
        }
      } catch {
        // Leave loaded=true so the popover shows a usable (empty) form rather
        // than spinning; the save call surfaces any real error.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, loaded, slug]);

  // Reset cached status when the slug changes (param-only route transitions).
  useEffect(() => {
    setLoaded(false);
    setSupported(true);
    setCurrentId(null);
    setError(null);
  }, [slug]);

  const save = async (): Promise<void> => {
    const id = input.trim();
    if (!id || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/admin/integration-catalog/${encodeURIComponent(slug)}/composio-auth-config`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify({ authConfigId: id }),
        },
      );
      if (res.ok) {
        const data = (await res.json()) as { authConfigId: string | null };
        setCurrentId(data.authConfigId ?? id);
        setInput("");
        setOpen(false);
        return;
      }
      if (res.status === 422) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        setError(
          body?.message ??
            "Composio rejected this id — check it belongs to this toolkit and to the gateway's Composio project.",
        );
        return;
      }
      setError(`Save failed (${res.status}) — try again.`);
    } catch {
      setError("Save failed — try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.adminMenu} ref={ref}>
      <button
        type="button"
        className={styles.adminMenuTrigger}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        Auth config
        {/* Neutral until a config is known to be registered (incl. before the
            lazy first-open fetch, when currentId is still null), green once set. */}
        <span className={currentId ? styles.authConfigDotOk : styles.authConfigDotNone} aria-hidden>
          ●
        </span>
      </button>
      {open ? (
        <div className={`${styles.adminMenuPopover} ${styles.authConfigPopover}`} role="dialog">
          <span className={styles.adminMenuLabel}>Composio auth config</span>
          {!loaded ? (
            <span className={styles.authConfigCurrent}>Loading…</span>
          ) : !supported ? (
            <span className={styles.authConfigCurrent}>
              Not applicable — this integration isn&apos;t connected through Composio.
            </span>
          ) : (
            <>
              <span className={styles.authConfigCurrent}>
                {currentId ?? "Not registered — paste the ac_… id from the Composio dashboard."}
              </span>
              <input
                className={styles.authConfigInput}
                type="text"
                placeholder="ac_…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void save();
                }}
                disabled={saving}
                spellCheck={false}
              />
              {error !== null ? <span className={styles.authConfigError}>{error}</span> : null}
              <button
                type="button"
                className={styles.authConfigSave}
                onClick={() => void save()}
                disabled={saving || input.trim() === ""}
              >
                {saving ? "Validating…" : currentId ? "Replace" : "Register"}
              </button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

// IP-171: "Available tools" — the concrete functions this integration
// exposes, in plain language. Fetched per-slug from the public catalog detail
// endpoint (independent of the tenant-scoped integrations fetch). Renders
// nothing until there's something worth showing, so it never flashes an empty
// heading while loading or for integrations with no synced tools yet.
// One tool's live permission state + the mutation handlers, supplied by the
// parent only when the account is connected and the feature is on. When this is
// null, ToolsSection is the read-only "Available tools" list (the pre-connect
// view); when present, the SAME list transforms in place — each row gains an
// Allow/Deny toggle and an integration-level default appears on top.
// The defaultPermission + per-tool overlay (keyed by the catalog tool slug so it
// lines up row-for-row with the read-only view) is the PermissionOverlay shape;
// this adds the live mutation state + handlers ToolsSection drives.
type ToolPermissionControls = PermissionOverlay & {
  canEdit: boolean;
  saving: boolean;
  error: boolean;
  onSet: (toolSlug: string | null, permission: ToolPermission) => void;
  onClear: (toolSlug: string) => void;
};

const ToolsSection = ({
  title,
  status,
  tools,
  permissions = null,
  permissionsLoading = false,
  permissionsLoadFailed = false,
}: {
  title: string;
  status: ToolsStatus;
  tools: ReadonlyArray<IntegrationTool>;
  // null = read-only catalog view; set = the connected, manage-permissions view.
  permissions?: ToolPermissionControls | null;
  // True when the manage view belongs here but its rules are still loading on a
  // cold cache — render the manage layout with skeleton toggles rather than the
  // misleading "Once X is connected" read-only copy for an already-connected app.
  permissionsLoading?: boolean;
  // True when the manage view SHOULD be here but its rules fetch failed — the
  // list stays read-only, and this explains why instead of failing silently.
  permissionsLoadFailed?: boolean;
}): JSX.Element | null => {
  const [query, setQuery] = useState<string>("");
  const [page, setPage] = useState<number>(0);

  // Snap back to the first page whenever the filter changes, so a narrow
  // result set never leaves you stranded on an out-of-range page.
  useEffect(() => {
    setPage(0);
  }, [query]);

  if (status !== "ready" || tools.length === 0) return null;

  // "action(s)" once you can manage them (it's a verb you're permitting),
  // "function(s)" in the read-only catalog view. The manage framing also applies
  // while the rules are loading (the controls are coming, just not landed yet).
  const managing: boolean = permissions !== null || permissionsLoading;
  const loadingControls: boolean = permissions === null && permissionsLoading;
  const noun: string = managing ? "action" : "function";
  const nounPlural = (n: number): string => (n === 1 ? noun : `${noun}s`);

  // A search box only earns its place once the list is long enough to be
  // annoying to scan (GitHub has 800+). Below that, the plain list is enough.
  const showSearch: boolean = tools.length > TOOLS_PER_PAGE;
  const needle: string = query.trim().toLowerCase();
  const searching: boolean = needle !== "";

  const matched = searching
    ? tools.filter(
        (t) =>
          t.name.toLowerCase().includes(needle) ||
          t.slug.toLowerCase().includes(needle) ||
          (t.description ?? "").toLowerCase().includes(needle),
      )
    : tools;

  // Paginate the (optionally filtered) set. safePage guards against `page`
  // briefly exceeding the range on the render before the reset effect fires.
  const totalPages: number = Math.max(1, Math.ceil(matched.length / TOOLS_PER_PAGE));
  const safePage: number = Math.min(page, totalPages - 1);
  const start: number = safePage * TOOLS_PER_PAGE;
  const visible = matched.slice(start, start + TOOLS_PER_PAGE);
  const showPager: boolean = matched.length > TOOLS_PER_PAGE;

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Available tools</h2>
      <div className={styles.toolsBody}>
        <p className={styles.toolsLead}>
          {searching
            ? `${matched.length.toLocaleString()} of ${tools.length.toLocaleString()} ${nounPlural(tools.length)} match.`
            : managing
              ? `Permissions apply to ${tools.length.toLocaleString()} ${title} ${nounPlural(tools.length)}. Denied ${noun}s are hidden from your agent.`
              : `Once ${title} is connected, your agent can use these ${tools.length.toLocaleString()} ${nounPlural(tools.length)}:`}
        </p>
        {permissions !== null ? (
          <p className={styles.toolsLead}>
            These rules cover this integration across all connected accounts on this instance.
            Actions without an override follow the default. Saved changes apply within ten seconds.
            Rules stay saved when you disconnect; you can edit them here before reconnecting.
            {!permissions.canEdit ? " Only owners and managers can change permissions." : ""}
          </p>
        ) : null}
        {permissions !== null ? (
          <div className={styles.permDefaultRow}>
            <span className={styles.permDefaultLabel}>Default for all actions</span>
            <PermissionToggle
              value={permissions.defaultPermission}
              disabled={permissions.saving || !permissions.canEdit}
              label={`Default permission for ${title}`}
              onChange={(next) => permissions.onSet(null, next)}
            />
          </div>
        ) : loadingControls ? (
          <div className={styles.permDefaultRow}>
            <span className={styles.permDefaultLabel}>Default for all actions</span>
            <Skeleton width="7.5rem" height="2rem" radius="0.5rem" />
          </div>
        ) : null}
        {permissions !== null && permissions.error ? (
          <p className={styles.permError} role="status">
            Couldn&apos;t save that change. Please try again.
          </p>
        ) : null}
        {permissions === null && permissionsLoadFailed ? (
          <p className={styles.permError} role="status">
            Couldn&apos;t load your tool permission settings — your saved rules still apply, but
            they can&apos;t be shown or changed right now. Reload the page to try again.
          </p>
        ) : null}
        {showSearch ? (
          <input
            type="search"
            className={styles.toolSearch}
            placeholder={`Search ${noun}s...`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={`Search ${title} ${noun}s`}
          />
        ) : null}
        {visible.length === 0 ? (
          <p className={styles.toolsLead}>No {noun}s match "{query.trim()}".</p>
        ) : (
          <ul className={styles.toolList}>
            {visible.map((tool) => {
              if (permissions === null && loadingControls) {
                // Manage layout with a skeleton toggle: names are already loaded
                // (separate catalog fetch), only the rules overlay is in flight,
                // so this mirrors the final row and nothing reflows when it lands.
                return (
                  <li key={tool.slug} className={styles.permTool}>
                    <div className={styles.permToolText}>
                      <span className={styles.toolName}>{tool.name}</span>
                      {tool.description !== null && tool.description !== "" ? (
                        <span className={styles.toolDesc}>{tool.description}</span>
                      ) : null}
                    </div>
                    <div className={styles.permControl}>
                      <Skeleton width="7.5rem" height="2rem" radius="0.5rem" />
                    </div>
                  </li>
                );
              }
              if (permissions === null) {
                return (
                  <li key={tool.slug} className={styles.tool}>
                    <span className={styles.toolName}>{tool.name}</span>
                    {tool.description !== null && tool.description !== "" ? (
                      <span className={styles.toolDesc}>{tool.description}</span>
                    ) : null}
                  </li>
                );
              }
              const { permission, explicit } = effectiveToolPermission(permissions, tool.slug);
              return (
                <li key={tool.slug} className={styles.permTool}>
                  <div className={styles.permToolText}>
                    <span className={styles.toolName}>{tool.name}</span>
                    {tool.description !== null && tool.description !== "" ? (
                      <span className={styles.toolDesc}>{tool.description}</span>
                    ) : null}
                  </div>
                  <div className={styles.permControl}>
                    <PermissionToggle
                      value={permission}
                      disabled={permissions.saving || !permissions.canEdit}
                      label={`Permission for ${tool.name}`}
                      onChange={(next) => permissions.onSet(tool.slug, next)}
                    />
                    {explicit && permissions.canEdit ? (
                      <button
                        type="button"
                        className={styles.permReset}
                        disabled={permissions.saving || !permissions.canEdit}
                        onClick={() => permissions.onClear(tool.slug)}
                      >
                        Reset to default
                      </button>
                    ) : (
                      <span className={styles.permInherited}>{explicit ? "custom rule" : "following default"}</span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {showPager ? (
          <div className={styles.pager}>
            <button
              type="button"
              className={styles.pagerButton}
              onClick={() => setPage(Math.max(0, safePage - 1))}
              disabled={safePage === 0}
            >
              Previous
            </button>
            <span className={styles.pagerStatus}>
              {start + 1}-{start + visible.length} of {matched.length}
            </span>
            <button
              type="button"
              className={styles.pagerButton}
              onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
              disabled={safePage >= totalPages - 1}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
};

// A two-state Allow / Deny segmented control. Step 1 ships allow/deny only; the
// public API accepts the same two values.
const PERMISSION_CHOICES: ReadonlyArray<{ value: ToolPermission; label: string }> = [
  { value: "allow", label: "Allow" },
  { value: "deny", label: "Deny" },
];

const PermissionToggle = ({
  value,
  disabled,
  onChange,
  label,
}: {
  value: ToolPermission;
  disabled: boolean;
  onChange: (next: ToolPermission) => void;
  label: string;
}): JSX.Element => (
  <div className={styles.permToggle} role="group" aria-label={label}>
    {PERMISSION_CHOICES.map((choice) => (
      <button
        key={choice.value}
        type="button"
        className={`${styles.permOption} ${value === choice.value ? styles.permOptionActive : ""}`}
        aria-pressed={value === choice.value}
        disabled={disabled}
        onClick={() => {
          if (value !== choice.value) onChange(choice.value);
        }}
      >
        {choice.label}
      </button>
    ))}
  </div>
);

// The connect panel's "how you authenticate" header. Reworded per auth method
// (replaces the old generic "How it ships" copy). Shows the API-key guide link
// when we have one. Always rendered for a not-yet-connected integration, so
// the customer knows what connecting will involve even with the flag off.
const AuthMethod = ({
  slug,
  title,
  authType,
  keyGuide,
}: {
  slug: string;
  title: string;
  authType: string | null;
  keyGuide?: { href: string; linkLabel: string } | undefined;
}): JSX.Element => {
  const apiKeyCopy = apiKeyConnectCopy(slug, title);
  const body: JSX.Element =
    authType === AUTH_TYPE.apiKey ? (
      <p className={connect.ctaText}>
        <strong>{apiKeyCopy.lead}</strong> {apiKeyCopy.body}
      </p>
    ) : authType === AUTH_TYPE.oauth2 ? (
      <p className={connect.ctaText}>
        <strong>OAuth.</strong> Authorise {title} in a secure window, no
        passwords pass through us, and you can disconnect any time.
      </p>
    ) : (
      <p className={connect.ctaText}>
        <strong>Guided setup.</strong> We'll wire up the {title} connection with
        you when it's ready.
      </p>
    );

  return (
    <div className={styles.authMethod}>
      <span className={connect.ctaEyebrow}>// AUTHENTICATION</span>
      {body}
      {authType === AUTH_TYPE.apiKey && keyGuide ? (
        <a
          className={styles.authLink}
          href={keyGuide.href}
          target="_blank"
          rel="noreferrer noopener"
        >
          {keyGuide.linkLabel}
          <span aria-hidden>→</span>
        </a>
      ) : null}
    </div>
  );
};

export const IntegrationDetail = (): JSX.Element => {
  const { slug = "" } = useParams<{ slug: string }>();

  // The tenant whose Composio identity we connect against. Prefer a RUNNING
  // claw; fall back to any the user owns. Null = no claw yet (button hidden).
  // Resolved before useIntegrations so we can fetch the tenant-scoped view
  // (which carries connectionStatus per IP-152) rather than the catalog.
  const { claws } = useClaws();
  const tenantId: string | null =
    claws.find((c) => c.status === "RUNNING")?.tenantId ?? claws[0]?.tenantId ?? null;

  const { integrations, status, refetch } = useIntegrations(tenantId);
  // An admin can pin one published resource to an integration as its connect
  // walkthrough. Reuses the dashboard-wide resources fetch (small, cached)
  // rather than a per-integration endpoint.
  const { resources } = useResources();
  const { summary, register } = useInterest("integration", slug);

  const integration: Integration | undefined = useMemo(
    () => integrations.find((i) => i.slug === slug),
    [integrations, slug],
  );
  // IP-247: the tenant's connected accounts for this integration. The ref mirror
  // (assigned below, after the ref is declared) lets the tab-watch / expiry
  // callbacks read the latest without re-binding the poll effect.
  // Message-channel connections now live in their own table (/api/channels), set
  // up from the Message Channels section, so an integration's connections here are
  // purely tool/business-app connections.
  const connections = integration?.connections ?? [];

  // IP-171: tool list, fetched once here and shared by the hero meta chip
  // ("N tools") and the "Available tools" section, so we don't fetch
  // the public detail endpoint twice.
  const { status: toolsStatus, tools, apiKeyFields, appUrl } = useIntegrationTools(slug);

  const [note, setNote] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [connecting, setConnecting] = useState<boolean>(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  // IP-206: a non-error note (e.g. "that attempt timed out"), shown near the
  // connect button. Distinct from connectError so a timeout doesn't read as a
  // hard failure.
  const [connectNotice, setConnectNotice] = useState<string | null>(null);
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState<boolean>(false);
  // IP-247: multi-account. `addingAccount` shows the "name this account" prompt;
  // `newAlias` is the name being typed. `actioningConnId` marks the card whose
  // disconnect/cancel is in flight (per-card spinner). `apiKeyAlias` carries the
  // account label into the API-key modal submit.
  const [addingAccount, setAddingAccount] = useState<boolean>(false);
  const [newAlias, setNewAlias] = useState<string>("");
  const [actioningConnId, setActioningConnId] = useState<string | null>(null);
  const apiKeyAliasRef = useRef<string | null>(null);
  // IP-422: self-serve BYO OAuth credentials modal (customer-owned app).
  const [byoModalOpen, setByoModalOpen] = useState<boolean>(false);
  // Pre-connect step (e.g. Monday.com's Composio-app install): the explainer
  // modal shown before the connect dispatch, and the alias it holds while the
  // user completes the step.
  const [preConnectOpen, setPreConnectOpen] = useState<boolean>(false);
  const preConnectAliasRef = useRef<string | null>(null);
  // IP-206: the OAuth tab we opened + the interval watching for the user
  // closing it. Refs (not state) so the watcher reads the latest without
  // re-rendering or restarting the poll effect.
  const oauthTabRef = useRef<Window | null>(null);
  const tabWatcherRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // IP-247: the alias of the in-flight OAuth connect, so the tab-watch / expiry
  // cancel can resolve which pending connection to retire; and a live mirror of
  // the connections list so those callbacks read the latest without re-binding.
  const pendingAliasRef = useRef<string | null>(null);
  const connectionsRef = useRef<Connection[]>([]);
  connectionsRef.current = connections;

  const isApiKey: boolean = integration?.authType === AUTH_TYPE.apiKey;
  const apiKeyCta: string = apiKeyConnectCopy(integration?.slug ?? "", integration?.title ?? "").cta;
  // IP-422: server-derived — true only while the tenant must supply their own
  // app credentials (no tenant config registered yet). Once registered, the
  // server flips this off and Connect follows the plain OAuth path.
  const isByoOauth: boolean = integration?.byoOauthPrompt === true;
  // IP-422: a tenant config already exists. Connect stays plain OAuth, but we
  // keep the modal reachable via "use different app credentials" so a config
  // created from bad credentials isn't a dead end (re-submit replaces it).
  const hasByoConfig: boolean = integration?.byoOauthConfigured === true;
  const guide: ResourceView | undefined = useMemo(
    () => pickIntegrationGuide(resources, slug),
    [resources, slug],
  );

  const { user } = useAuth();
  const isAdmin: boolean = user?.isAdmin === true;
  const [togglingConfigured, setTogglingConfigured] = useState<boolean>(false);
  const connectEnabled: boolean = flagEnabled(
    user?.features,
    FEATURE_FLAGS.integrationsConnectEnabled,
  );
  // IP-352: when the customer billing surface is on, gate the connect CTA on
  // the tenant's integration-slot allowance (out of slots -> buy-a-slot upsell).
  const billingEnabled: boolean = flagEnabled(user?.features, FEATURE_FLAGS.billingCustomer);
  // Rollout eligibility belongs to the instance owner and is resolved by the API.
  // Saved rules remain enforceable after disconnection, so keep them manageable.
  const toolPermissionsEnabled = tenantId !== null;
  const perms = useToolPermissions(tenantId, slug, toolPermissionsEnabled);
  // Fold the rules into the controls ToolsSection overlays onto the SAME list.
  // Null until ready, so the list shows read-only first, then the toggles light
  // up in place once the rules load — the section transforms, never duplicates.
  const permissionControls: ToolPermissionControls | null = useMemo(() => {
    if (!toolPermissionsEnabled || perms.status !== "ready" || perms.data === null) return null;
    // Guard against a cross-integration overlay: useCachedQuery keeps the
    // previous route's data on a key change (status stays 'ready') while the new
    // slug's rules are still in flight, which would paint integration A's toggles
    // onto integration B's tools. Only overlay rules that belong to THIS slug.
    if (perms.data.slug !== slug) return null;
    // The overlay carries ONLY explicit rules (presence => explicit). Tools with
    // no rule fall back to defaultPermission/explicit:false in ToolsSection (via
    // effectiveToolPermission).
    return {
      ...buildPermissionOverlay(perms.data),
      canEdit: perms.data.canEdit,
      saving: perms.saving,
      error: perms.error !== null,
      onSet: (toolSlug, permission) => void perms.setPermission(toolSlug, permission),
      onClear: (toolSlug) => void perms.clearPermission(toolSlug),
    };
  }, [toolPermissionsEnabled, perms.status, perms.data, perms.saving, perms.error, perms.setPermission, perms.clearPermission]);
  // V49: the server-computed, caller-specific availability is the single
  // gating signal; `configured` is derived from it for the existing branches
  // (badge, connect CTA, auth-method panel). fromApi owns the legacy
  // configured->availability fallback and always sets the field, so this is a
  // plain read (the `?? "available"` only guards the no-integration state).
  const availability: IntegrationAvailability = integration?.availability ?? "available";
  const configured: boolean = availability === "available";
  const canConnect: boolean = connectEnabled && canStartConnect(availability, connections);
  // The admin's raw override behind that effective flag (null = auto), so the
  // admin menu can show which of Auto / Connectable / Not-connectable is set.
  const configuredOverride: boolean | null = integration?.configuredOverride ?? null;
  // 'guided' integrations (Telegram) have no OAuth/API-key connect of their own;
  // their only setup was the channel flow, which now lives in Message Channels.
  // So the detail page suppresses the connect block for them and points to the
  // channel section instead.
  const isGuided: boolean = integration?.setupKind === "guided";
  // Message channels (WhatsApp/Telegram/Slack/Teams/Discord) are set up from the
  // Message Channels section now, not here. We keep these in the catalog as agent
  // TOOLS, so WhatsApp's OAuth connect stays; we just cross-link the chat-channel
  // setup and drop the in-page channel panels.
  const isMessageChannel: boolean = MESSAGE_CHANNEL_SLUGS.has(integration?.slug ?? "");

  const userRegistered: boolean =
    (summary?.userRegistered ?? integration?.status === "requested") === true;
  const interestedCount: number = summary?.interestedCount ?? 0;

  // IP-206: resolve an in-flight connect the user didn't finish.
  //   "abandoned" — they closed the OAuth tab (or hit Cancel). Reverts silently.
  //   "expired"   — the connect ran past Composio's ~10-min window. Shows a note.
  // The server live-checks the upstream first, so if the user actually completed
  // the OAuth and only THEN closed the tab, the row is kept + activated rather
  // than deleted. We always refetch so the UI matches the server's verdict.
  const handleCancel = useCallback(
    async (reason: "abandoned" | "expired"): Promise<void> => {
      if (tenantId === null) return;
      // Stop watching + close the OAuth tab if it's still open.
      if (tabWatcherRef.current) {
        clearInterval(tabWatcherRef.current);
        tabWatcherRef.current = null;
      }
      oauthTabRef.current?.close();
      oauthTabRef.current = null;
      // Resolve which pending row(s) to retire. On "expired" the whole poll
      // window lapsed, so every still-pending attempt is stale (and after a
      // reload pendingAliasRef is empty, so we can't single one out anyway). On
      // "abandoned" cancel the one we connected with, by the alias we kept in
      // the ref — falling back to the sole pending attempt when the ref was lost
      // to a reload.
      const pending = connectionsRef.current.filter((c) => c.status === "pending");
      const targets =
        reason === "expired"
          ? pending
          : ((): Connection[] => {
              const byAlias = pending.find((c) => c.alias === pendingAliasRef.current);
              const one = byAlias ?? (pending.length === 1 ? pending[0] : undefined);
              return one ? [one] : [];
            })();
      pendingAliasRef.current = null;
      let anyCancelled = false;
      for (const target of targets) {
        try {
          const res = await fetch(
            `/api/integrations/${encodeURIComponent(tenantId)}/connections/${encodeURIComponent(target.connectionId)}/cancel`,
            { method: "POST", credentials: "include", headers: { ...getCsrfHeader() } },
          );
          if (res.ok) {
            const body = (await res.json().catch(() => null)) as { status?: string } | null;
            if (body?.status === "cancelled") anyCancelled = true;
          }
        } catch {
          // Best-effort: a failed cancel just leaves the row for the server's
          // expiry sweep to retire. The refetch below still syncs the UI.
        }
      }
      await refetch();
      setConnecting(false);
      if (reason === "expired" && anyCancelled) {
        setConnectNotice(
          "That connection attempt timed out before it finished. Reconnect to try again.",
        );
      }
    },
    [tenantId, refetch],
  );

  // IP-206: watch the OAuth tab; the moment the user closes it, cancel the
  // unfinished connect so the button reverts to Connect instead of spinning.
  const startOauthTabWatch = useCallback((): void => {
    if (tabWatcherRef.current) clearInterval(tabWatcherRef.current);
    tabWatcherRef.current = setInterval(() => {
      if (oauthTabRef.current && oauthTabRef.current.closed) {
        void handleCancel("abandoned");
      }
    }, OAUTH_TAB_WATCH_INTERVAL_MS);
  }, [handleCancel]);

  // IP-92/IP-247: mint a Connect Link for this tenant + toolkit (optionally for a
  // NAMED account via `alias`), then hand off to Composio's hosted OAuth page in
  // a new tab (the dashboard stays put). alias null = the primary/first account.
  const handleConnect = async (alias: string | null): Promise<void> => {
    if (tenantId === null) return;
    if (!connectEnabled) return;
    // Open the tab synchronously, inside the click gesture — the redirect URL
    // only arrives after the await below, and a window.open() then would be
    // popup-blocked. Detach opener so the OAuth page can't reach back into us.
    const oauthTab = window.open("about:blank", "_blank");
    if (oauthTab) oauthTab.opener = null;
    setConnecting(true);
    setConnectError(null);
    setConnectNotice(null);
    setAddingAccount(false);
    setNewAlias("");
    pendingAliasRef.current = alias;
    try {
      const res = await fetch(
        `/api/integrations/${encodeURIComponent(tenantId)}/${encodeURIComponent(slug)}/connect`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify({ alias }),
        },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? `Connect failed (${res.status})`);
      }
      const { redirectUrl } = (await res.json()) as { redirectUrl: string };
      if (oauthTab) {
        oauthTab.location.replace(redirectUrl);
        oauthTabRef.current = oauthTab;
        startOauthTabWatch();
      } else {
        // Pre-open was blocked; fall back to a direct open (no tab handle to
        // watch — the poll cap + server sweep still resolve it).
        window.open(redirectUrl, "_blank", "noopener,noreferrer");
      }
      // IP-206: reflect the freshly-written pending row immediately so the
      // button flips to "Connecting…" without a manual refresh; the poll effect
      // then takes over.
      await refetch();
      setConnecting(false);
    } catch (err) {
      oauthTab?.close();
      setConnectError(err instanceof Error ? err.message : String(err));
      setConnecting(false);
    }
  };

  // API-key connect: POST the credential values to ztc-core, which has the
  // gateway validate them before storing. `credentials` is one value per schema
  // field. Returns a result the modal renders inline; on success we refetch so
  // the page flips to "Connected" (or "pending" while the poller confirms).
  const handleApiKeySubmit = async (
    credentials: Record<string, string>,
  ): Promise<ApiKeySubmitResult> => {
    if (tenantId === null) return { ok: false, message: "No instance available yet." };
    try {
      const res = await fetch(
        `/api/integrations/${encodeURIComponent(tenantId)}/${encodeURIComponent(slug)}/connect-api-key`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify({ credentials, alias: apiKeyAliasRef.current }),
        },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        return { ok: false, message: body?.message ?? `Connect failed (${res.status})` };
      }
      apiKeyAliasRef.current = null;
      setAddingAccount(false);
      setNewAlias("");
      await refetch();
      return { ok: true };
    } catch (err) {
      return { ok: false, message: err instanceof Error ? err.message : String(err) };
    }
  };

  // IP-422 self-serve BYO OAuth: POST the customer's own app credentials; the
  // gateway creates their per-tenant auth config and returns the OAuth
  // redirect. Same synchronous-tab dance as handleConnect (popup blockers),
  // same result shape as the API-key modal so errors render inline. The form
  // submit is a user gesture, so the pre-open isn't blocked.
  const handleByoOauthSubmit = async (
    credentials: Record<string, string>,
  ): Promise<ApiKeySubmitResult> => {
    if (tenantId === null) return { ok: false, message: "No instance available yet." };
    const oauthTab = window.open("about:blank", "_blank");
    if (oauthTab) oauthTab.opener = null;
    try {
      const res = await fetch(
        `/api/integrations/${encodeURIComponent(tenantId)}/${encodeURIComponent(slug)}/connect-byo-oauth`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify({ credentials }),
        },
      );
      if (!res.ok) {
        oauthTab?.close();
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        return { ok: false, message: body?.message ?? `Connect failed (${res.status})` };
      }
      const { redirectUrl } = (await res.json()) as { redirectUrl: string };
      if (oauthTab) {
        oauthTab.location.replace(redirectUrl);
        oauthTabRef.current = oauthTab;
        startOauthTabWatch();
      } else {
        window.open(redirectUrl, "_blank", "noopener,noreferrer");
      }
      pendingAliasRef.current = null;
      await refetch();
      return { ok: true };
    } catch (err) {
      oauthTab?.close();
      return { ok: false, message: err instanceof Error ? err.message : String(err) };
    }
  };

  // IP-206: disconnect a live integration (soft delete). ztc-core tears down
  // the upstream account then keeps our row as 'disconnected' (reconnect re-runs
  // the connect flow). Available even while it's working. On success we refetch
  // so the page flips back to the Connect affordance.
  const handleDisconnect = async (connectionId: string): Promise<void> => {
    if (tenantId === null) return;
    setActioningConnId(connectionId);
    setConnectError(null);
    setConnectNotice(null);
    try {
      const res = await fetch(
        `/api/integrations/${encodeURIComponent(tenantId)}/connections/${encodeURIComponent(connectionId)}/disconnect`,
        { method: "POST", credentials: "include", headers: { ...getCsrfHeader() } },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? `Disconnect failed (${res.status})`);
      }
      await refetch();
    } catch (err) {
      setConnectError(err instanceof Error ? err.message : String(err));
    } finally {
      setActioningConnId(null);
    }
  };

  // IP-247: cancel one pending account by id (per-card Cancel). Distinct from
  // handleCancel (the in-flight OAuth tab-watch/expiry path).
  const cancelConnection = async (connectionId: string): Promise<void> => {
    if (tenantId === null) return;
    setActioningConnId(connectionId);
    setConnectError(null);
    try {
      const res = await fetch(
        `/api/integrations/${encodeURIComponent(tenantId)}/connections/${encodeURIComponent(connectionId)}/cancel`,
        { method: "POST", credentials: "include", headers: { ...getCsrfHeader() } },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? `Cancel failed (${res.status})`);
      }
      // The response carries the row's settled status. Still 'pending' means the
      // server could not confirm the attempt was abandoned and deliberately left
      // the row alone, so say that: re-rendering "connecting..." unchanged is
      // what made a failing Cancel look like a dead button.
      const body = (await res.json().catch(() => null)) as { status?: string } | null;
      if (body?.status === "pending") {
        setConnectError("We couldn't cancel that just now. Please try again in a moment.");
      }
      await refetch();
    } catch (err) {
      setConnectError(err instanceof Error ? err.message : String(err));
    } finally {
      setActioningConnId(null);
    }
  };

  // IP-255: rename one account's alias. ztc-core validates + enforces uniqueness
  // and the ">=2 accounts" rule; on success we refetch so the card label (and the
  // gateway namespace, on the tenant's next tools/list) reflect it. Returns the
  // outcome so the card can show a collision/validation message inline.
  const handleRename = async (
    connectionId: string,
    alias: string,
  ): Promise<{ ok: boolean; message?: string }> => {
    if (tenantId === null) return { ok: false, message: "No instance available yet." };
    try {
      const res = await fetch(
        `/api/integrations/${encodeURIComponent(tenantId)}/connections/${encodeURIComponent(connectionId)}/rename`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify({ alias }),
        },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        return { ok: false, message: body?.message ?? `Rename failed (${res.status})` };
      }
      await refetch();
      return { ok: true };
    } catch (err) {
      return { ok: false, message: err instanceof Error ? err.message : String(err) };
    }
  };

  // IP-247: dispatch a connect for a card. OAuth goes straight to
  // handleConnect; API-key opens the modal carrying the alias. Used by both
  // "Reconnect" (the card's alias) and "Add another account" (the new name).
  // IP-422: BYO first-connect opens the credentials modal instead. It can only
  // be true with no config registered — i.e. no connections/cards exist yet —
  // so it's always a primary connect (alias is null in practice).
  const dispatchConnect = (alias: string | null): void => {
    if (isByoOauth) {
      setByoModalOpen(true);
    } else if (isApiKey) {
      apiKeyAliasRef.current = alias;
      setApiKeyModalOpen(true);
    } else {
      void handleConnect(alias);
    }
  };

  // Some integrations need a manual step in the third-party app before any
  // connect can succeed (config/preConnectSteps.ts — e.g. Monday.com only
  // accepts the connection once the Composio app is installed there). Those
  // get the explainer modal first, on every connect path: reconnects hit the
  // same wall if the app was uninstalled, and the modal's "already done it?
  // just continue" note keeps the repeat case cheap.
  const preConnectStep: PreConnectStep | undefined = PRE_CONNECT_STEPS[slug];
  const startConnect = (alias: string | null): void => {
    setArrival(null);
    if (preConnectStep) {
      preConnectAliasRef.current = alias;
      setPreConnectOpen(true);
    } else {
      dispatchConnect(alias);
    }
  };

  // Admin-only: set the connectability state. The menu's four options map
  // 1:1 onto the server's ConnectabilityState enum, so the write always
  // specifies the complete state (no partial-update ambiguity). Hits the
  // admin catalog endpoint, then refetches so the badge + connect block
  // update.
  const handleSetOverride = async (choice: ConnectabilityChoice): Promise<void> => {
    setTogglingConfigured(true);
    try {
      const res = await fetch(
        `/api/admin/integration-catalog/${encodeURIComponent(slug)}/connectability`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify({ connectability: choice.key }),
        },
      );
      if (res.ok) await refetch();
    } finally {
      setTogglingConfigured(false);
    }
  };

  // IP-152/IP-206: while the connection is mid-connect (`pending`), poll the
  // per-tenant integrations endpoint to pick up the gateway poller's flip to
  // active / error. The cadence relaxes after CONNECTION_POLL_RELAX_AFTER_MS so
  // a long wait doesn't hammer the API. When the window (CONNECTION_POLL_
  // DURATION_MS, past Composio's expiry) elapses with the row still pending, we
  // cancel the lapsed attempt so the UI reverts on its own. Keyed on the live
  // connectionStatus, so it auto-starts whenever a (re)load lands in 'pending'
  // and auto-stops the moment it terminates.
  const connectionStatus: ConnectionStatus | null = integration?.connectionStatus ?? null;
  // IP-247: poll while ANY account is mid-connect, not just the primary.
  const hasPending: boolean = connections.some((c) => c.status === "pending");
  useEffect(() => {
    if (!hasPending) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startedAt: number = Date.now();
    const tick = async (): Promise<void> => {
      if (cancelled) return;
      const elapsed: number = Date.now() - startedAt;
      if (elapsed >= CONNECTION_POLL_DURATION_MS) {
        // Outlasted Composio's OAuth window — retire the lapsed attempt.
        await handleCancel("expired");
        return;
      }
      await refetch();
      if (cancelled) return;
      const next: number =
        elapsed >= CONNECTION_POLL_RELAX_AFTER_MS
          ? CONNECTION_POLL_RELAXED_INTERVAL_MS
          : CONNECTION_POLL_INTERVAL_MS;
      timer = setTimeout(() => void tick(), next);
    };
    timer = setTimeout(() => void tick(), CONNECTION_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [hasPending, refetch, handleCancel]);

  // IP-206: stop watching the OAuth tab if the page unmounts mid-connect.
  useEffect(
    () => () => {
      if (tabWatcherRef.current) clearInterval(tabWatcherRef.current);
    },
    [],
  );

  // ztc-core's OAuth redirects land here with ?installed=1 (a marketplace
  // install, nothing linked yet), ?connected=1 or ?error=<code>. Remember the
  // arrival for the banner and drop the params so a refresh doesn't repeat it.
  const [searchParams, setSearchParams] = useSearchParams();
  const [arrival, setArrival] = useState<Arrival | null>(null);
  useEffect(() => {
    const next = readArrival(searchParams);
    if (next === null) return;
    setArrival(next);
    setSearchParams(
      (params) => {
        for (const key of ARRIVAL_PARAMS) params.delete(key);
        return params;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  const handleRegister = async (): Promise<void> => {
    setSubmitting(true);
    setError(null);
    const result = await register(note);
    setSubmitting(false);
    if (!result.ok) {
      setError(result.message);
    } else {
      setNote("");
    }
  };

  if (status === "loading" && !integration) {
    return (
      <div className={styles.page}>
        <p className={styles.loading}>Loading…</p>
      </div>
    );
  }

  if (!integration) {
    return (
      <div className={styles.page}>
        <SavedPermissionRecovery policy={perms} tools={[]} unavailable />
        <p className={styles.notFound}>
          {perms.data ? "" : "We couldn't find that integration."}
          <br />
          <Link className={styles.back} to="/dashboard/integrations">
            ← Integrations catalog
          </Link>
        </p>
      </div>
    );
  }

  const valueProps =
    VALUE_PROPS_BY_CATEGORY[integration.category] ?? fallbackValueProps(integration);

  const isActive = integration.status === "active";
  // IP-247: any connection rows -> render the per-account cards.
  const hasConnections: boolean = connections.length > 0;
  // First-connect CTA: flag on, an instance to connect against, a ready-to-go
  // integration, no connection rows yet, and not a legacy bundled/guided one.
  // Once a connection exists (even pending) the cards branch takes over.
  const showConnect: boolean =
    connectEnabled && configured && tenantId !== null && !isGuided && !hasConnections && !isActive;
  const showInterest: boolean = !isGuided && !hasConnections && !isActive && !showConnect;
  const arrivalText: string | null =
    arrival === null
      ? null
      : arrivalNotice(arrival, {
          title: integration.title,
          connected: connections.some((c) => c.status === "active"),
          canConnect: showConnect || hasConnections,
        });

  // IP-217: "where do I get my API key" link for the auth header + connect
  // modal. URL embedded in a field description > app homepage; undefined when
  // we have neither (the per-field hints still guide).
  const keyGuide = isApiKey
    ? resolveKeyGuide(integration.title, apiKeyFields, appUrl)
    : undefined;

  return (
    <div className={styles.page}>
      {/* Two destinations rather than a "back" link — we don't track where the
          user came from, so we offer both the dashboard and the catalog. Reads
          as a breadcrumb trail ending in the integration's own title (the hero
          heading below). */}
      <nav className={styles.topNav} aria-label="Breadcrumb">
        <Link className={styles.crumb} to="/dashboard/overview">
          Dashboard
        </Link>
        <span className={styles.crumbSep} aria-hidden>
          /
        </span>
        <Link className={styles.crumb} to="/dashboard/integrations">
          Integrations catalog
        </Link>
      </nav>

      <Hero
        integration={integration}
        configured={configured}
        toolCount={toolsStatus === "ready" ? tools.length : null}
        showBadge={connectEnabled}
        adminControl={
          connectEnabled && isAdmin ? (
            <>
              <AdminAuthConfigControl slug={slug} />
              <AdminConnectabilityMenu
                override={configuredOverride}
                adminOnly={integration.adminOnly === true}
                busy={togglingConfigured}
                onSelect={(choice) => void handleSetOverride(choice)}
              />
            </>
          ) : undefined
        }
      />

      <div className={styles.body}>
        <section className={styles.left}>
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>What it does, once connected</h2>
            <ul className={styles.bullets}>
              {valueProps.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>

          {/* Withheld until the catalog tool list has settled: an empty `tools`
              while it is still loading would claim the integration is
              unavailable and list every live rule as retired. */}
          {toolsStatus === "loading" ? null : (
            <SavedPermissionRecovery policy={perms} tools={tools} unavailable={toolsStatus === "error" || tools.length === 0} />
          )}
          <ToolsSection
            title={integration.title}
            status={toolsStatus}
            tools={tools}
            permissions={permissionControls}
            permissionsLoading={
              toolPermissionsEnabled && permissionControls === null && perms.status === "loading"
            }
            permissionsLoadFailed={toolPermissionsEnabled && perms.status === "error"}
          />
        </section>

        <aside className={styles.right}>
          {arrivalText !== null ? (
            <p
              className={arrival?.kind === "error" ? styles.arrivalNoticeError : styles.arrivalNotice}
              role="status"
            >
              {arrivalText}
            </p>
          ) : null}
          {/* Message channels (how you TALK TO the agent) are set up from the
              Message Channels section now, not here. Cross-link there so the
              catalog detail page isn't a dead end. WhatsApp keeps its tool/OAuth
              connect below; Telegram (guided) has no tool connect, so the
              !isGuided gate hides the connect block for it entirely. */}
          {isMessageChannel ? (
            <Link to="/dashboard/overview" className={styles.channelCrossLink}>
              <span>
                Want to chat with your agent on {integration.title}? Set it up in
                Message Channels
              </span>
              <span aria-hidden>→</span>
            </Link>
          ) : null}

          {!isGuided ? (
            <div className={styles.cta}>
            {hasConnections ? (
              <>
                {/* Service notice: the integration is `unavailable` (often an
                    upstream/Composio-side change) while this tenant still
                    holds a LIVE connection — flag that usage may be degraded.
                    `restricted` (admin-gated, nothing broken) deliberately
                    does NOT trigger this; pending/error/disconnected-only
                    states already communicate themselves on the cards. */}
                {availability === "unavailable" && connections.some((c) => c.status === "active") ? (
                  <p className={styles.serviceNotice} role="status">
                    {integration.title} is temporarily unavailable for new
                    connections, and existing connections may be affected. If
                    something isn&apos;t working, it should recover once the
                    underlying issue is resolved.
                  </p>
                ) : null}
                <ConnectedAccounts
                  connections={connections}
                  title={integration.title}
                  connectEnabled={canConnect}
                  connecting={connecting}
                  actioningConnId={actioningConnId}
                  addingAccount={addingAccount}
                  newAlias={newAlias}
                  connectError={connectError}
                  connectNotice={connectNotice}
                  onReconnect={startConnect}
                  onDisconnect={(id) => void handleDisconnect(id)}
                  onCancel={(id) => void cancelConnection(id)}
                  onRename={handleRename}
                  onStartAdd={() => {
                    setAddingAccount(true);
                    setNewAlias("");
                  }}
                  onAliasChange={setNewAlias}
                  onSubmitAdd={() => startConnect(newAlias.trim())}
                  onCancelAdd={() => {
                    setAddingAccount(false);
                    setNewAlias("");
                  }}
                />
              </>
            ) : isActive ? (
              <p className={connect.ctaText}>
                <strong>Connected.</strong> Manage it from your instance dashboard.
              </p>
            ) : (
              <>
                <AuthMethod
                  slug={integration.slug}
                  title={integration.title}
                  authType={integration.authType}
                  keyGuide={keyGuide}
                />

                {showConnect ? (
                  billingEnabled && tenantId !== null ? (
                    <SlotGate tenantId={tenantId}>
                      <ConnectCta
                        title={integration.title}
                        isApiKey={isApiKey}
                        apiKeyCta={apiKeyCta}
                        connecting={connecting}
                        connectionStatus={connectionStatus}
                        connectError={connectError}
                        connectNotice={connectNotice}
                        onConnect={() => startConnect(null)}
                        onCancel={() => void handleCancel("abandoned")}
                      />
                    </SlotGate>
                  ) : (
                    <ConnectCta
                      title={integration.title}
                      isApiKey={isApiKey}
                      apiKeyCta={apiKeyCta}
                      connecting={connecting}
                      connectionStatus={connectionStatus}
                      connectError={connectError}
                      connectNotice={connectNotice}
                      onConnect={() => startConnect(null)}
                      onCancel={() => void handleCancel("abandoned")}
                    />
                  )
                ) : null}

                {/* IP-422: escape hatch back into the credentials modal once a
                    config exists — Connect reuses the stored config, so a bad
                    first submit would otherwise be unfixable self-serve. */}
                {showConnect && hasByoConfig && !connecting ? (
                  <button
                    type="button"
                    className={styles.byoRetryLink}
                    onClick={() => setByoModalOpen(true)}
                  >
                    Use different app credentials
                  </button>
                ) : null}

                {showInterest ? (
                  <InterestForm
                    title={integration.title}
                    userRegistered={userRegistered}
                    interestedCount={interestedCount}
                    note={note}
                    submitting={submitting}
                    error={error}
                    onNoteChange={setNote}
                    onRegister={() => void handleRegister()}
                  />
                ) : null}
              </>
            )}
            </div>
          ) : null}

          {/* Deliberately outside the connect block: a walkthrough stays useful
              after connecting (reconnects, or a forgotten step), so it must not
              be hidden by connection state or by setupKind. */}
          {guide !== undefined ? (
            <IntegrationGuide resource={guide} integrationTitle={integration.title} />
          ) : null}

          <a
            className={styles.scope}
            href={CAL_BOOK_20MIN_URL}
            target="_blank"
            rel="noreferrer noopener"
          >
            <span>Want to talk first? Book a 20-min call</span>
            <span aria-hidden>→</span>
          </a>
        </aside>
      </div>

      {isApiKey ? (
        <ApiKeyConnectModal
          open={apiKeyModalOpen}
          title={integration.title}
          fields={apiKeyFields}
          // The modal only renders the help line (and its link) when helpText is
          // set, so gate it on having a resolved guide link. Per-field "where to
          // look" hints still render under each input from their descriptions.
          helpText={keyGuide ? "Need a key?" : undefined}
          helpHref={keyGuide?.href}
          helpLinkLabel={keyGuide?.linkLabel}
          onClose={() => setApiKeyModalOpen(false)}
          onSubmit={handleApiKeySubmit}
        />
      ) : null}

      {preConnectStep ? (
        <PreConnectStepModal
          open={preConnectOpen}
          title={integration.title}
          step={preConnectStep}
          onClose={() => setPreConnectOpen(false)}
          onContinue={() => {
            setPreConnectOpen(false);
            const alias = preConnectAliasRef.current;
            preConnectAliasRef.current = null;
            dispatchConnect(alias);
          }}
        />
      ) : null}

      {isByoOauth || hasByoConfig ? (
        // IP-422: BYO OAuth credentials modal — same generic form, fixed
        // client-id/secret fields plus the setup preamble (redirect URL).
        // Also mounted when a config already exists, for the re-entry link.
        <ApiKeyConnectModal
          open={byoModalOpen}
          title={integration.title}
          fields={BYO_OAUTH_FIELDS}
          preamble={<ByoOauthPreamble title={integration.title} />}
          onClose={() => setByoModalOpen(false)}
          onSubmit={handleByoOauthSubmit}
        />
      ) : null}
    </div>
  );
};
